import {mapRange} from "../functions/math";
import {StackNotifier} from "./StackNotifier";
import {uID} from "../functions/generic";
import {IS} from "./IS";
import {Calc} from "./Math/Calc";

/**
 * @deprecated - Use ClockV2
 * Clock
 * ---
 * Advanced interval with start, stop, pause and tick report
 */
export class Clock {
	constructor() {
		this.interval = null;
	}

	/**
	 * Start clock
	 * ---
	 * @param {Number} duration Duration in ms
	 * @param {Function} onCompletedCallback Completed callback
	 * @param {function({duration: Number, elapsed: Number, progress: Number})} onTickCallback Callback for every interval tick
	 * @param {Number} tickInterval Interval that is used for tickCallback
	 */
	start(duration, onCompletedCallback = () => null, onTickCallback = () => null, tickInterval = 10) {
		this.stop();

		this.duration = duration;
		this.onCompletedCallback = onCompletedCallback;
		this.onTickCallback = onTickCallback;
		this.tickInterval = tickInterval;

		this._createInterval(tickInterval);
	}

	/**
	 * Pause
	 * ---
	 */
	pause() {
		clearInterval(this.interval);
	}

	/**
	 * Unpause
	 * ---
	 */
	unpause() {
		this._createInterval(this.tickInterval);
	}

	/**
	 * Stop
	 * ---
	 * Stops and resets the clock
	 */
	stop() {
		this.pause();
		this.ticksElapsed = 0;
	}

	/**
	 * Set callback
	 * ---
	 * Callback changer (can be changed at any time; even at runtime)
	 * @param {Function} onCompletedCallback
	 * @param {function({duration: Number, elapsed: Number, progress: Number})} onTickCallback
	 */
	setCallbacks(onCompletedCallback = () => null, onTickCallback = () => null) {
		onCompletedCallback && (this.onCompletedCallback = onCompletedCallback);
		onTickCallback && (this.onTickCallback = onTickCallback);
	}

	/**
	 * @private
	 * Create interval
	 * ---
	 * @param {Number} tickInterval Interval in ms >= 1
	 */
	_createInterval(tickInterval = 1) {
		this.interval = setInterval(() => this._onIntervalTick(), tickInterval || 1);
	}

	/**
	 * @private
	 * On interval tick
	 * ---
	 * Tick interval reporter
	 */
	_onIntervalTick() {
		let {duration, tickInterval} = this;

		this.ticksElapsed++;
		let msElapsed = this.ticksElapsed * tickInterval;

		this.onTickCallback({
			duration,
			elapsed: msElapsed,
			progress: mapRange(msElapsed, 0, duration, 0, 100),
		});

		if(msElapsed >= duration) {
			this.stop();
			this.onCompletedCallback();
		}
	}
}

export class ClockV2 {
	/**
	 * @constructor
	 * @param {number} tickFrequency How often the interval should check for alerts and breakpoints and update the elapsed time
	 * @param {number} [automaticBreakpointTime=Infinity] If > 0 and < Infinity, sets a breakpoint for the clock to automatically stop
	 * @param {boolean} keepAlertStatesAfterStop When the clock stops, whether to reset the triggered states.
	 * @param {boolean} keepBreakpointStatesAfterStop When the clock stops, whether to reset the breakpoints states.
	 */
	constructor(tickFrequency = 1000, {automaticBreakpointTime = Infinity, keepAlertStatesAfterStop = false, keepBreakpointStatesAfterStop = false} = {}) {
		this._tickFrequency = tickFrequency;
		this._keepAlertStatesAfterStop = keepAlertStatesAfterStop;
		this._keepBreakpointStatesAfterStop = keepBreakpointStatesAfterStop;

		//null and undefined are both different states! null = paused; undefined = stopped
		this._interval = undefined;
		this._stackNotifier = new StackNotifier();

		this._alerts = [];
		this._breakpoints = [];

		if(automaticBreakpointTime !== Infinity) {
			this.stopAt(automaticBreakpointTime);
		}

		this._elapsed = 0;
	}

	/**
	 * Is stopped
	 * ---
	 * Returns if the clock has stopped for good.
	 * @return {boolean}
	 */
	get isStopped() {
		return this._interval === undefined;
	}

	/**
	 * Is paused
	 * ---
	 * Returns if the clock is paused
	 * @return {boolean}
	 */
	get isPaused() {
		return this._interval === null;
	}

	/**
	 * Elapsed
	 * ---
	 * Returns elapsed time
	 * **Warning!** Elapsed time is calculated every clock tick,
	 * which means that the real elapsed time will be different.
	 * @return {number}
	 */
	get elapsed() {
		return this._elapsed;
	}

	/**
	 * Closest alert
	 * ---
	 * Returns closest alert
	 * @return {ClockAlert}
	 */
	get closestAlert() {
		if(this._alerts.length < 1) return;

		return this._alerts.reduce((previousAlert, currentValue) => currentValue.getNextTriggerTime() < previousAlert.getNextTriggerTime());
	}

	/**
	 * Closes breakpoint
	 * ---
	 * Returns closest breakpoint
	 * @return {Object}
	 */
	get closestBreakpoint() {
		if(this._breakpoints.length < 1) return;

		return this._breakpoints.reduce((previousBreakpoint, currentBreakpoint) => currentBreakpoint.triggerTime < previousBreakpoint.triggerTime);
	}

	/**
	 * Progress
	 * ---
	 * Returns 0-100 (%) or NaN if there is not breakpoint (clock won't stop by itself)
	 * @returns {number|NaN} progress in %
	 */
	get progress() {
		const closestBreakpoint = this.closestBreakpoint;

		if(closestBreakpoint) {
			return Calc.mapRangeClamped(this.elapsed, 0, closestBreakpoint.triggerTime, 0, 100);
		}

		return NaN;
	}

	/**
	 * On start
	 * ---
	 * Adds a callback for the start event
	 * @param {function} callback
	 */
	onStart(callback) {
		this._stackNotifier.on("start", callback, true);
	}

	/**
	 * On pause
	 * ---
	 * Adds a callback for the pause event
	 * @param {function} callback
	 */
	onPause(callback) {
		this._stackNotifier.on("pause", callback, true);
	}

	/**
	 * On stop
	 * ---
	 * Adds a callback for the stop event
	 * @param {function} callback
	 */
	onStop(callback) {
		this._stackNotifier.on("stop", callback, true);
	}

	/**
	 * On any alert event
	 * ---
	 * Adds a callback for any alert event
	 * @param {function} callback
	 */
	onAnyAlert(callback) {
		this._stackNotifier.on("alert", callback, true);
	}

	/**
	 * On any breakpoint
	 * ---
	 * Adds a callback for any breakpoint event
	 * @param {function} callback
	 */
	onAnyBreakpoint(callback) {
		this._stackNotifier.on("breakpoint", callback, true);
	}

	/**
	 * On tick
	 * ---
	 * Adds a callback for tick
	 * @param {function} callback
	 */
	onTick(callback) {
		this._stackNotifier.on("tick", callback, true);
	}

	/**
	 * Start
	 * ---
	 * Starts the clock
	 */
	start() {
		this._start();
	}

	/**
	 * Pause
	 * ---
	 * Pauses the clock
	 */
	pause() {
		this._pause();
	}

	/**
	 * Stop
	 * ---
	 * Stops the clock
	 */
	stop() {
		this._stop();
	}

	/**
	 * Stop at
	 * ---
	 * Stops the clock at specific time (same as createBreakpointAt)
	 * @param {number} triggerTime Specific time (in ms) when to stop the clock
	 * @param {function} callback
	 * @see ClockV2.createBreakpointAt
	 */
	stopAt(triggerTime, callback = () => null) {
		this.createBreakpointAt(triggerTime, callback);
	}

	/**
	 * Stop in
	 * ---
	 * Stops the clock in **timeOffset** ms (same as createBreakpointIn)
	 * @param {number} timeOffset How long to wait (in ms) until the clock should stop
	 * @param {function} callback
	 * @see ClockV2.createBreakpointIn
	 */
	stopIn(timeOffset, callback = () => null) {
		this.createBreakpointIn(timeOffset, callback);
	}

	/**
	 * Create alert at
	 * ---
	 * Creates an alert at a specific time
	 * @param {number} triggerTime Specific time (in ms) when to fire the alert
	 * @param {function} callback
	 */
	createAlertAt(triggerTime, callback) {
		this._addAlert(triggerTime, callback);
	}

	/**
	 * Create alert in
	 * ---
	 * Creates an alert in **timeOffset** ms from now
	 * @param {number} timeOffset How long to wait (in ms) until the alert should fire
	 * @param {function} callback
	 */
	createAlertIn(timeOffset, callback) {
		this._addAlert(this.elapsed + timeOffset, callback);
	}

	/**
	 * Create alert every
	 * ---
	 * Creates an alert **every** ms from now
	 * @param {number} every time (in ms) how often to fire the alert
	 * @param {function} callback
	 */
	createAlertEvery(every, callback) {
		this._addAlert(this.elapsed, callback, every);
	}

	/**
	 * Create alert every at
	 * ---
	 * Creates an alert **every** ms from the startTime
	 * @param {number} startTime Specific time (in ms) when to fire the alert
	 * @param {number} every time (in ms) how often to fire the alert
	 * @param {function} callback
	 */
	createAlertEveryAt(startTime, every, callback) {
		this._addAlert(startTime, callback, every);
	}

	/**
	 * Create alert every in
	 * ---
	 * Creates an alert **every** ms in **timeOffset** ms from now
	 * @param {number} timeOffset How long to wait (in ms) until the alert should fire
	 * @param {number} every time (in ms) how often to fire the alert
	 * @param {function} callback
	 */
	createAlertEveryIn(timeOffset, every, callback) {
		this._addAlert(this.elapsed + timeOffset, callback, every);
	}

	/**
	 * Create breakpoint at
	 * ---
	 * Creates a breakpoint (stops the clock) at a specific time.
	 * @param {number} triggerTime Specific time (in ms) when to stop the clock
	 * @param {function} callback
	 */
	createBreakpointAt(triggerTime, callback) {
		this._addBreakpoint(triggerTime, callback);
	}

	/**
	 * Create breakpoint in
	 * ---
	 * Creates a breakpoint (stops the clock) in **timeOffset** from now
	 * @param {number} timeOffset How long to wait (in ms) until the breakpoint should stop the clock
	 * @param {function} callback
	 */
	createBreakpointIn(timeOffset, callback) {
		this._addBreakpoint(this.elapsed + timeOffset, callback);
	}

	_start() {
		const resumed = this.isPaused;

		clearInterval(this._interval);
		this._interval = setInterval(() => {
			this._elapsed+= this._tickFrequency;

			this._stackNotifier.notify("tick");

			this._handleTick();
		}, this._tickFrequency);

		this._stackNotifier.notify("start");

		if(!resumed) {
			this._handleTick();
		}
	}

	_pause() {
		clearInterval(this._interval);
		this._interval = null;//null represents paused state
		this._stackNotifier.notify("pause");
	}

	_stop() {
		clearInterval(this._interval);
		this._interval = undefined;//undefined represents stopped state
		this._elapsed = 0;

		this._stackNotifier.notify("stop");

		!this._keepAlertStatesAfterStop && this._alerts.forEach(alert => alert.reset());
		!this._keepBreakpointStatesAfterStop && this._breakpoints.forEach(breakpoint => breakpoint.triggered = false);
	}

	_handleTick() {
		this._processAlerts();
		this._processBreakpoints();
	}

	_addAlert(triggerTime, callback, repeatEvery = -1) {
		this._alerts.push(new ClockAlert({
			triggerTime,
			callback,
			repeatEvery,
		}));
	}

	_addBreakpoint(triggerTime, callback) {
		this._breakpoints.push({
			id: uID(),
			triggerTime,
			callback,
			triggered: false,
		});
	}

	_processAlerts() {
		this._alerts.forEach(alert => {
			const nextTriggerTime = alert.getNextTriggerTime();

			if(IS.number(nextTriggerTime) && nextTriggerTime <= this.elapsed) {
				alert.trigger();

				this._stackNotifier.notify("alert", alert);
			}
		});
	}

	_processBreakpoints() {
		let shouldStop = false;

		this._breakpoints.forEach(breakpoint => {
			if(!breakpoint.triggered && breakpoint.triggerTime <= this.elapsed) {
				shouldStop = true;

				breakpoint.triggered = true;

				this._stackNotifier.notify("breakpoint", breakpoint);
			}
		});

		if(shouldStop) {
			this.stop();
		}
	}
}

class ClockAlert {
	constructor({triggerTime, callback, repeatEvery}) {
		this.id = uID();
		this.triggerTime = triggerTime;
		this.callback = callback;
		this.repeatEvery = repeatEvery;
		this.repeated = 0;
		this.lastTriggerTime = null;
	}

	get shouldRepeat() {
		return this.repeatEvery > 0;
	}

	get triggered() {
		return this.lastTriggerTime !== null;
	}

	getNextTriggerTime() {
		//Alert was triggered already and is not repeatable
		if(this.triggered && !this.shouldRepeat) return;

		//Alert was triggered already but repeats every N ms
		if(this.triggered) {
			return this.lastTriggerTime + this.repeatEvery * this.repeated;
		}

		//Alert is still waiting to be triggered for the first time
		return this.triggerTime;
	}

	trigger(...callbackParams) {
		this.lastTriggerTime = this.getNextTriggerTime();
		this.repeated++;

		IS.fnc(this.callback) && this.callback(...callbackParams);
	}

	reset() {
		this.lastTriggerTime = null;
		this.repeated = 0;
	}
}
